<template>
  <div class="bot-chats">
    <p class="grey--text mt-4">
      Усі, хто написав у Telegram-бот Платформи<template v-if="botUsername"> (<a :href="'https://t.me/' + botUsername" target="_blank" rel="noopener">@{{ botUsername }}</a>)</template>:
      психологи і клієнти. Помічник відповідає за базою знань із розділу «ШІ-агент». Що не може вирішити, передає Вам: Вам приходить лист, а чат позначається «Потрібна відповідь».
    </p>
    <p v-if="loaded && !botReady" class="red--text">Токен бота на сервері не задано: бот не приймає повідомлень.</p>
    <p v-else-if="loaded && !aiReady" class="red--text">Ключ OpenAI на сервері не задано: помічник не відповідає.</p>

    <div class="bot-chats__toolbar">
      <v-switch
        v-model="aiEnabled"
        class="mt-0 pt-0"
        hide-details
        label="Помічник відповідає в боті"
        :disabled="!loaded"
        @change="saveAi"
      ></v-switch>
      <v-select v-model="filter" :items="filters" label="Показати" outlined dense hide-details class="bot-chats__filter" @change="load"></v-select>
      <v-text-field v-model.trim="search" label="Пошук за іменем" outlined dense hide-details clearable class="bot-chats__search" @input="searchLater"></v-text-field>
    </div>

    <div class="bot-chats__panes" :class="{ 'bot-chats__panes--open': current }">
      <ul class="bot-chats__list">
        <li v-if="loaded && !chats.length" class="bot-chats__empty">Чатів ще немає</li>
        <li v-for="chat in chats" :key="chat.id">
          <button
            type="button"
            class="bot-chats__item"
            :class="{ 'bot-chats__item--active': current && current.id === chat.id }"
            @click="open(chat.id)"
          >
            <span class="bot-chats__line">
              <span class="bot-chats__name">{{ chat.name }}</span>
              <span class="bot-chats__date">{{ short(chat.last_message_at) }}</span>
            </span>
            <span class="bot-chats__line">
              <span class="bot-chats__preview">{{ chat.last_text }}</span>
              <span v-if="chat.unread" class="bot-chats__unread">{{ chat.unread }}</span>
            </span>
            <span class="bot-chats__tags">
              <span class="bot-chats__tag">{{ chat.psychologist ? "Психолог" : "Без анкети" }}</span>
              <span v-if="chat.needs_human" class="bot-chats__tag bot-chats__tag--human">Потрібна відповідь</span>
            </span>
          </button>
        </li>
      </ul>

      <section class="bot-chats__dialog">
        <p v-if="!current" class="bot-chats__hint">Оберіть чат зліва</p>
        <template v-else>
          <header class="bot-chats__head">
            <v-btn icon class="bot-chats__back" aria-label="До списку" @click="current = null"><v-icon>mdi-arrow-left</v-icon></v-btn>
            <div class="bot-chats__who">
              <b>{{ current.name }}</b>
              <span v-if="current.username" class="grey--text">
                <a :href="'https://t.me/' + current.username" target="_blank" rel="noopener">@{{ current.username }}</a>
              </span>
              <span v-if="current.blocked" class="red--text caption">Заблокували бота</span>
            </div>
            <div class="bot-chats__actions">
              <v-btn v-if="current.needs_human" small text color="primary" @click="post('done')">Питання закрито</v-btn>
              <v-btn small text @click="post('assistant', { paused: !current.ai_paused })">
                {{ current.ai_paused ? "Повернути помічника" : "Вимкнути помічника тут" }}
              </v-btn>
            </div>
          </header>

          <div ref="messages" class="bot-chats__messages">
            <div v-for="message in current.messages" :key="message.id" class="bot-chats__msg" :class="'bot-chats__msg--' + message.sender">
              <span class="bot-chats__author">{{ authors[message.sender] }} · {{ human(message.created_at) }}</span>
              <div class="bot-chats__bubble">{{ message.text }}</div>
              <span v-if="message.handoff" class="caption orange--text">передано Вам</span>
              <span v-if="message.failed" class="caption red--text">не доставлено</span>
            </div>
          </div>

          <p v-if="current.ai_paused" class="bot-chats__paused">Помічник у цьому чаті мовчить. Після Вашої відповіді він вмикається сам через 12 годин.</p>
          <div class="bot-chats__composer">
            <v-textarea
              v-model="reply"
              placeholder="Ваша відповідь"
              outlined
              dense
              auto-grow
              rows="1"
              hide-details
              counter="3500"
              maxlength="3500"
              class="bot-chats__input"
              @keydown.enter.exact.prevent="send"
            ></v-textarea>
            <v-btn color="primary" depressed :loading="sending" :disabled="!reply.trim()" @click="send">Надіслати</v-btn>
          </div>
          <p v-if="error" class="red--text caption mt-2 mb-0">{{ error }}</p>
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import ApiClient from "@/libs/http-client";

const POLL_MS = 15000;

export default {
  name: "CrmBotChats",
  data() {
    return {
      chats: [],
      current: null,
      loaded: false,
      aiEnabled: true,
      aiReady: true,
      botReady: true,
      botUsername: "",
      filter: "all",
      search: "",
      reply: "",
      sending: false,
      error: "",
      timer: null,
      searchTimer: null,
      filters: [
        { text: "Усі чати", value: "all" },
        { text: "Потрібна відповідь", value: "human" },
        { text: "Психологи", value: "psychologists" },
        { text: "Без анкети", value: "clients" },
      ],
      authors: { user: "Людина", ai: "Помічник", admin: "Ви", bot: "Бот" },
    };
  },
  mounted() {
    this.load();
    this.timer = setInterval(this.refresh, POLL_MS);
  },
  beforeDestroy() {
    clearInterval(this.timer);
    clearTimeout(this.searchTimer);
  },
  methods: {
    load() {
      const params = { filter: this.filter };
      if (this.search) params.search = this.search;

      return ApiClient.get("api/admin/crm/telegram-chats", { params }).then((response) => {
        this.chats = response.data.data;
        const meta = response.data.meta;
        this.aiEnabled = meta.ai_enabled;
        this.aiReady = meta.ai_ready;
        this.botReady = meta.bot_ready;
        this.botUsername = meta.bot_username;
        this.loaded = true;
      });
    },
    // the open chat follows new messages; what the admin is typing stays
    refresh() {
      this.load();
      if (this.current) this.open(this.current.id, true);
    },
    searchLater() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.load, 400);
    },
    open(id, quiet) {
      if (!quiet) this.error = "";

      return ApiClient.get(`api/admin/crm/telegram-chats/${id}`).then((response) => {
        this.show(response.data.data);
      });
    },
    show(chat) {
      const same = this.current && this.current.id === chat.id;
      const more = !same || this.current.messages.length !== chat.messages.length;
      this.current = chat;
      const item = this.chats.find((row) => row.id === chat.id);
      if (item) Object.assign(item, { unread: 0, needs_human: chat.needs_human });
      if (more) {
        this.$nextTick(() => {
          const box = this.$refs.messages;
          if (box) box.scrollTop = box.scrollHeight;
        });
      }
    },
    send() {
      const text = this.reply.trim();
      if (!text || this.sending) return;
      this.sending = true;
      this.error = "";
      ApiClient.post(`api/admin/crm/telegram-chats/${this.current.id}/reply`, { text })
        .then((response) => {
          this.reply = "";
          this.show(response.data.data);
          this.load();
        })
        .catch((error) => {
          this.error = (error && error.message) || "Не вдалося надіслати";
        })
        .finally(() => {
          this.sending = false;
        });
    },
    post(action, body = {}) {
      ApiClient.post(`api/admin/crm/telegram-chats/${this.current.id}/${action}`, body).then((response) => {
        this.show(response.data.data);
        this.load();
      });
    },
    saveAi() {
      ApiClient.post("api/admin/crm/telegram-chats/settings", { ai_enabled: this.aiEnabled });
    },
    short(value) {
      if (!value) return "";
      const date = new Date(value);
      const today = date.toDateString() === new Date().toDateString();
      return today
        ? date.toLocaleTimeString("uk-UA", { hour: "2-digit", minute: "2-digit" })
        : date.toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit" });
    },
    human(value) {
      return value ? new Date(value).toLocaleString("uk-UA", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
    },
  },
};
</script>

<style lang="scss" scoped>
$teal: #009fb3;

.bot-chats {
  max-width: 1200px;
  margin: 0 auto;
  &__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px 24px;
    margin-bottom: 16px;
  }
  &__filter {
    flex: 0 1 220px;
  }
  &__search {
    flex: 0 1 260px;
  }
  &__panes {
    display: grid;
    grid-template-columns: 340px minmax(0, 1fr);
    height: 640px;
    border: 1px solid #ddd;
    border-radius: 8px;
    overflow: hidden;
  }
  &__list {
    margin: 0;
    padding: 0;
    overflow-y: auto;
    border-right: 1px solid #ddd;
    list-style: none;
  }
  &__empty,
  &__hint {
    padding: 24px;
    color: #888;
  }
  &__item {
    display: block;
    width: 100%;
    padding: 10px 14px;
    border-bottom: 1px solid #eee;
    text-align: left;
    &:hover {
      background: #f7f9fa;
    }
    &--active {
      background: #e8f6f8;
    }
  }
  &__line {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }
  &__name {
    overflow: hidden;
    font-weight: 500;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  &__date {
    flex: 0 0 auto;
    color: #888;
    font-size: 12px;
  }
  &__preview {
    overflow: hidden;
    color: #666;
    font-size: 13px;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  &__unread {
    flex: 0 0 auto;
    min-width: 20px;
    padding: 0 6px;
    border-radius: 10px;
    background: $teal;
    color: #fff;
    font-size: 12px;
    line-height: 20px;
    text-align: center;
  }
  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
  }
  &__tag {
    color: #888;
    font-size: 12px;
    &--human {
      color: #d9730d;
      font-weight: 500;
    }
  }
  &__dialog {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
  }
  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
    padding: 10px 14px;
    border-bottom: 1px solid #eee;
  }
  &__back {
    display: none;
  }
  &__who {
    display: flex;
    flex-direction: column;
    flex: 1 1 200px;
    min-width: 0;
  }
  &__actions {
    display: flex;
    flex-wrap: wrap;
  }
  &__messages {
    flex: 1 1 auto;
    min-height: 0;
    padding: 14px;
    overflow-y: auto;
    background: #f7f9fa;
  }
  &__msg {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-bottom: 12px;
    &--user {
      align-items: flex-start;
    }
    &--user &__bubble {
      background: #fff;
    }
    &--ai &__bubble {
      background: #e8f6f8;
    }
    &--admin &__bubble {
      background: #d8f0d8;
    }
    &--bot &__bubble {
      background: #eee;
    }
  }
  &__author {
    margin-bottom: 2px;
    color: #888;
    font-size: 12px;
  }
  &__bubble {
    max-width: 80%;
    padding: 8px 12px;
    border: 1px solid #e3e3e3;
    border-radius: 10px;
    white-space: pre-wrap;
    word-break: break-word;
  }
  &__paused {
    margin: 0;
    padding: 6px 14px;
    color: #888;
    font-size: 13px;
  }
  &__composer {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    padding: 10px 14px;
    border-top: 1px solid #eee;
  }
  &__input {
    flex: 1 1 auto;
    max-height: 160px;
    overflow-y: auto;
  }
}

@media (max-width: 900px) {
  .bot-chats {
    &__panes {
      grid-template-columns: minmax(0, 1fr);
      height: 70vh;
      min-height: 480px;
    }
    &__list {
      border-right: 0;
    }
    &__dialog {
      display: none;
    }
    &__panes--open &__list {
      display: none;
    }
    &__panes--open &__dialog {
      display: flex;
    }
    &__back {
      display: inline-flex;
    }
    &__filter,
    &__search {
      flex: 1 1 100%;
    }
    // iOS zooms the page on a smaller field
    ::v-deep input,
    ::v-deep textarea {
      font-size: 16px;
    }
  }
}
</style>
